


































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { ikonFriendsAndFamilyService } from '@/services/IkonFriendsAndFamilyService'
import { Getter } from 'vuex-class'
import IkonVouchersResponse from '@/models/IkonVouchersResponse'
import { registerEvent, unregisterEvent, AxEventType } from '@aspenware-library/event-hub'

@Component
export default class IkonFriendsAndFamilyViewComponent extends Vue {
  @Prop() readonly config: any
  @Getter('products/selectedProduct') product
  @Getter('products/dateAttributes') productDateAttributes

  private hasFetchedVouchers: boolean = false
  private errorFetchingVouchers: boolean = false
  private voucherResponse: IkonVouchersResponse | null = null
  private entitlementId: string | null = null
  private toastMessage: string | null = null
  private showToast: boolean = false
  private toastType: string = 'danger'
  private isLoading: boolean = false
  private displayComponent: boolean = false

  // Lifecycle Events
  async mounted() {
    // Register Event handlers
    registerEvent(AxEventType.AddToCart, this.handleAddToCart)
    registerEvent<void>(AxEventType.RemoveFromCart, this.handleRemoveFromCart)
  }

  async beforeDestroy() {
    // Unregister Event handlers
    unregisterEvent(AxEventType.AddToCart, this.handleAddToCart)
    unregisterEvent<void>(AxEventType.RemoveFromCart, this.handleRemoveFromCart)
  }

  // Methods
  private async handleAddToCart() {
    if (this.isIkonFamilyAndFriendsEnabled) {
      // Get the new voucher availability
      await this.fetchVouchers()

      this.setEntitlementId(false)
    }
  }

  private async handleRemoveFromCart() {
    if (this.isIkonFamilyAndFriendsEnabled) {
      // Get the new voucher availability
      await this.fetchVouchers()

      this.setEntitlementId(false)
    }
  }

  private async fetchVouchers() {
    try {
      this.isLoading = true
      this.voucherResponse = await ikonFriendsAndFamilyService.getVouchers()
      if (!this.voucherResponse?.IsLoggedIn) {
        this.voucherResponse = null
        this.hasFetchedVouchers = false
      } else {
        this.hasFetchedVouchers = true
        this.isLoading = false
      }
    } catch (e) {
      this.isLoading = false
      this.errorFetchingVouchers = true
      this.hasFetchedVouchers = true
      this.displayToast(this.config.errorRetrievingVouchersToast, 'danger')
    }
    this.displayComponent = true
  }

  private displayToast(message: string, type: string) {
    this.toastType = type
    this.toastMessage = message
    this.showToast = true
  }

  private getEntitlementId(): string | null {
    if (this.voucherResponse && this.productStartDate) {
      const date = this.productStartDate.toDateString()
      if (this.voucherResponse && this.voucherResponse?.LocalVouchers?.length > 0) {
        if (
          !this.localPassBlackoutDates ||
          (this.localPassBlackoutDates && !this.localPassBlackoutDates.find((d) => d.toDateString() == date))
        ) {
          return this.voucherResponse.LocalVouchers[0].entitlement_id.toString()
        }
      }

      if (this.voucherResponse && this.voucherResponse?.BaseVouchers?.length > 0) {
        if (
          !this.basePassBlackoutDates ||
          (this.basePassBlackoutDates && !this.basePassBlackoutDates.find((d) => d.toDateString() == date))
        ) {
          return this.voucherResponse.BaseVouchers[0].entitlement_id.toString()
        }
      }

      if (this.voucherResponse && this.voucherResponse?.BasePlusVouchers?.length > 0) {
        if (
          !this.basePlusPassBlackoutDates ||
          (this.basePlusPassBlackoutDates && !this.basePlusPassBlackoutDates.find((d) => d.toDateString() == date))
        ) {
          return this.voucherResponse.BasePlusVouchers[0].entitlement_id.toString()
        }
      }

      if (this.voucherResponse && this.voucherResponse?.FullVouchers?.length > 0) {
        if (
          !this.fullBlackoutDates ||
          (this.fullBlackoutDates && !this.fullBlackoutDates.find((d) => d.toDateString() == date))
        ) {
          return this.voucherResponse.FullVouchers[0].entitlement_id.toString()
        }
      }
    }

    if (this.isBlackoutDate && this.allVouchers.length > 0) {
      this.displayToast(this.config.errorNoAvailableVouchersForRestrictedDayToast, 'danger')
    } else {
      this.displayToast(this.config.errorNoAvailableVouchersToast, 'danger')
    }

    return null
  }

  private setEntitlementId(displayToast: boolean = true): void {
    // Reset toast if there is already one being displayed
    this.showToast = false

    if (this.hasFetchedVouchers) {
      this.entitlementId = this.getEntitlementId()
      if (displayToast && this.entitlementId) {
        this.displayToast(this.config.voucherAvailableToast, 'success')
      }
    } else {
      this.displayToast(this.config.errorNotLoggedInToast, 'danger')
    }

    this.$set(this.ikonEntitlementIdAttribute, 'SelectedValue', this.entitlementId)
  }

  // Computed Properties
  get isIkonFamilyAndFriendsEnabled() {
    return this.product?.Id == this.config.productId
  }

  get loginHref() {
    return `${this.config.loginDomain}${this.config.loginEndpoint}?response_type=code&client_id=${this.config.entitlementsClientId}&redirect_uri=${this.config.redirectUri}`
  }

  get logoutHref() {
    return `${this.config.loginDomain}/logout?response_type=code&client_id=${this.config.entitlementsClientId}&redirect_uri=${this.config.redirectUri}`
  }

  get productStartDate(): Date | null {
    const startDateAttribute = this.productDateAttributes?.find((i) => i.Name == 'Start Date')
    return startDateAttribute?.SelectedValueId != null ? new Date(startDateAttribute.SelectedValueId) : null
  }

  get ikonEntitlementIdAttribute() {
    return this.product ? this.product.ProductAttributes.find((attr) => attr.Name === 'Ikon Entitlement Id') : null
  }

  get hasVouchers() {
    if (this.voucherResponse) {
      if (this.allVouchers.length > 0) {
        return true
      }
    }
    return false
  }

  get allVouchers() {
    return [
      ...(this.voucherResponse?.LocalVouchers ?? []),
      ...(this.voucherResponse?.BaseVouchers ?? []),
      ...(this.voucherResponse?.BasePlusVouchers ?? []),
      ...(this.voucherResponse?.FullVouchers ?? []),
    ]
  }

  get localPassBlackoutDates(): Date[] | null {
    if (this.config.ikonLocalPassBlackoutDates != '') {
      return this.config.ikonLocalPassBlackoutDates.split(',').map((date) => new Date(date))
    }
    return null
  }

  get basePassBlackoutDates(): Date[] | null {
    if (this.config.ikonBasePassBlackoutDates != '') {
      return this.config.ikonBasePassBlackoutDates.split(',').map((date) => new Date(date))
    }
    return null
  }

  get basePlusPassBlackoutDates(): Date[] | null {
    if (this.config.ikonBasePlusPassBlackoutDates != '') {
      return this.config.ikonBasePlusPassBlackoutDates.split(',').map((date) => new Date(date))
    }
    return null
  }

  get fullBlackoutDates(): Date[] | null {
    if (this.config.ikonPassBlackoutDates != '') {
      return this.config.ikonPassBlackoutDates.split(',').map((date) => new Date(date))
    }
    return null
  }

  get isBlackoutDate(): boolean {
    if (!this.productStartDate) {
      return false
    }

    const allBlackoutDates = [
      ...(this.localPassBlackoutDates ?? []),
      ...(this.basePassBlackoutDates ?? []),
      ...(this.basePlusPassBlackoutDates ?? []),
      ...(this.fullBlackoutDates ?? []),
    ]
    return !!allBlackoutDates.find((d) => d.toDateString() == this.productStartDate?.toDateString())
  }

  get topLeftImage() {
    return !!this.config.titleImageUrl ? this.config.titleImageUrl : require('@/assets/images/text-redeem-IKON.png')
  }

  get bottomLeftImage() {
    return !!this.config.loginButtonImageUrl
      ? this.config.loginButtonImageUrl
      : require('@/assets/images/btn-login-IKON.png')
  }

  get bottomLeftSecondaryImage() {
    return !!this.config.logoutButtonImageUrl
      ? this.config.logoutButtonImageUrl
      : require('@/assets/images/btn-logout-IKON.png')
  }

  get middleImage() {
    return !!this.config.centerImageUrl ? this.config.centerImageUrl : require('@/assets/images/ikon-default-photo.jpg')
  }

  get rightImage() {
    return !!this.config.ikonLogoImageUrl ? this.config.ikonLogoImageUrl : require('@/assets/images/logo-IKON-gold.png')
  }

  // Watchers
  @Watch('productStartDate')
  async onProductStartDateChanged(to, from) {
    if (to != from && this.isIkonFamilyAndFriendsEnabled) {
      this.setEntitlementId()
    }
  }

  @Watch('isIkonFamilyAndFriendsEnabled')
  async onProductChanged(to, from) {
    if (to != from && to == true) {
      await this.fetchVouchers()
    }
  }
}
